import { useEffect, useMemo } from 'react'

import { CommonTableTemplate } from 'app/common/templates-next/table-template'
import { useCurrencySign } from 'app/common/components-next/currency'
import { Dish } from 'app/dataTypes/dishes/dish'
import { useSaleTypesAsArray } from 'app/dataProviders/SaleTypesData'
import { useDishesData } from 'app/dataProviders/dishes/DishesData'
import { DishId } from 'app/common/types/id'

import {
  DishesTableColumn,
  dishesTableColumnArrangement,
  dishesTableColumnCellProps,
  useDishesTableColumnHeaders,
} from './table/dishesTableProps'

import {
  dishesTableRenderHeaderCell,
  dishesTableRenderHeaderCellClear,
} from './table/dishesTableRenderHeaderCell'

import { useDishesTableRenderCell } from './table/dishesTableRenderCell'
import { features } from 'app/features'
import { useCachedItems } from './useCachedItems'

import styles from './DishesTable.module.scss'

export const DishesTable = ({
  locked = false,
  createTechCardLink,
  resetStickyShift,
  skeletonRows,
  pinnedDishesIds,
  keepCheckboxesRegisteredOnDispose = false,
  hideSelectAll = false,
  settings = {},
  onPinnedDishes,
}: {
  locked?: boolean
  createTechCardLink?: (dishId: DishId) => string
  resetStickyShift?: boolean
  skeletonRows?: number
  pinnedDishesIds?: Array<string>
  keepCheckboxesRegisteredOnDispose?: boolean
  hideSelectAll?: boolean
  settings?: Record<string, boolean>
  onPinnedDishes?: (dishes: Array<Dish>) => void
}) => {
  // "Технические карты", "Складской учет" придут из апи
  const techCards = features.dishes.barcode
  const warehouseAccounting = features.dishes.processingLevel

  const hidePhotoColumn = techCards || warehouseAccounting

  const [{ dishes, loading, nomenclatureActive }, { fieldUpdate, photoDelete, photoUpdate }] =
    useDishesData()

  const pinnedDishes = useCachedItems(pinnedDishesIds, dishes)

  useEffect(() => {
    if (onPinnedDishes) {
      onPinnedDishes(pinnedDishes.items)
    }
  }, [pinnedDishes]) // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => {
    return [...pinnedDishes.items, ...dishes.filter(({ id }) => !pinnedDishes.has(id))].map(
      ({ id, nomenclature, title, photoUrl, portion, cost, saleType }: Dish) => {
        return {
          [DishesTableColumn.Check]: title,
          [DishesTableColumn.Id]: id as DishId,
          [DishesTableColumn.Nomenclature]: nomenclature || '',
          [DishesTableColumn.ProcessingLevel]: 'TODO',
          [DishesTableColumn.Title]: title,
          [DishesTableColumn.Barcode]: '001237462877412',
          [DishesTableColumn.Photo]: photoUrl || '',
          [DishesTableColumn.Portion]: portion,
          [DishesTableColumn.Unit]: 'TODO',
          [DishesTableColumn.Cost]: cost,
          [DishesTableColumn.SaleType]: saleType.id,
          [DishesTableColumn.Options]: id,
        }
      },
    )
  }, [dishes, pinnedDishes])

  const combinedSettings = useMemo(() => {
    const { nomenclature, id, processingLevel, unit, barcode, photo, ...rest } = settings

    return {
      ...rest,
      [DishesTableColumn.Barcode]: warehouseAccounting && barcode && features.dishes.barcode,
      [DishesTableColumn.Photo]: !hidePhotoColumn && photo,
      [DishesTableColumn.ProcessingLevel]: processingLevel && features.dishes.processingLevel,
      [DishesTableColumn.Unit]: techCards && unit && features.dishes.unit,
      [DishesTableColumn.Nomenclature]: nomenclature && nomenclatureActive,
      [DishesTableColumn.Id]: id && nomenclatureActive,
    }
  }, [settings, nomenclatureActive])

  const { saleTypes, loading: saleTypesLoading } = useSaleTypesAsArray()

  const currency = useCurrencySign()
  const headers = useDishesTableColumnHeaders(currency)

  const dishesTableRenderCell = useDishesTableRenderCell({
    checkboxesAvailable: !!('check' in combinedSettings ? combinedSettings.check : true),
    keepCheckboxesRegisteredOnDispose,
    locked,
    saleTypes,
    saleTypesLoading,
    photoUpdate,
    photoDelete,
    fieldUpdate,
    createTechCardLink,
    showTechCardExtras: techCards,
    showPhotoInTitle: hidePhotoColumn,
  })

  return (
    <CommonTableTemplate
      data={data}
      dataArrange={dishesTableColumnArrangement}
      settings={combinedSettings}
      headerData={headers}
      headerCellProps={dishesTableColumnCellProps}
      loading={loading}
      renderCell={dishesTableRenderCell}
      renderHeaderCell={
        hideSelectAll ? dishesTableRenderHeaderCellClear : dishesTableRenderHeaderCell
      }
      cellProps={dishesTableColumnCellProps}
      placeholder="Нет записей, удовлетворяющих условиям"
      classNameTr={styles.row}
      stickyHeader
      resetStickyShift={resetStickyShift}
      skeletonRows={skeletonRows}
    />
  )
}
